import { useCallback, useMemo, useState } from 'react'

import { FieldTypes } from 'modules/contracts_settings/components/ContractFields/Taxonomies/taxonomyTranslator'
import { updateOptions } from 'modules/legal_entities/lib/updateOptions'
import { CustomFieldsField } from 'modules/legal_entities/pages/settings/ContractsLegalEntityPage/ContractFields/types'

const useCustomFieldHandler = (initialField: CustomFieldsField | undefined) => {
  const [field, setField] = useState<CustomFieldsField | undefined>(initialField)

  const handleInputChange = useCallback(
    (value: string) => {
      if (field) setField({ ...field, labelText: value })
    },
    [field]
  )

  const handleMandatoryChange = useCallback(
    (value: boolean) => {
      if (field) setField({ ...field, required: value })
    },
    [field]
  )

  const handleTypeChange = useCallback(
    (value: string) => {
      const newValue = FieldTypes[value]
      if (!!newValue && field) setField({ ...field, fieldType: newValue })
    },
    [field]
  )

  const handleAddOption = useCallback(() => {
    if (field) {
      const newOptions = [...field.options]
      newOptions.push({ draft: true, label: '', isActive: true })
      setField({ ...field, options: newOptions })
    }
  }, [field])

  const handleOptionAction = useCallback(
    (action: 'delete' | 'edit' | 'archive', id: string, value?: string) => {
      if (!field) return
      const index = field.options.findIndex((opt) => opt.id === id)
      const finalIndex = index !== -1 ? index : Number(id)
      const newOptions = updateOptions(field.options, action, finalIndex, value)
      setField({ ...field, options: newOptions })
    },
    [field]
  )

  const handlers = useMemo(
    () => ({
      field,
      setField,
      handleInputChange,
      handleMandatoryChange,
      handleTypeChange,
      handleAddOption,
      handleOptionAction,
    }),
    [
      field,
      setField,
      handleInputChange,
      handleMandatoryChange,
      handleTypeChange,
      handleAddOption,
      handleOptionAction,
    ]
  )

  return handlers
}

export default useCustomFieldHandler
